import * as Phaser from "phaser-ce"
import { COLOR } from "../../../common/Color"
import { log } from "../../../common/Logging"
import { FontType } from "../../../common/TextUtils"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { ButtonContainer, Buttons } from "../../generic-sprites/Buttons"
import { ExpandoSprite } from "../../generic-sprites/ExpandoSprite"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { Audio } from "../../utils/Audio"
import { ColorUtils } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Transitions } from "../transitions/Transitions"
import { DefinitionAudio, DefinitionImages, loadAssets } from "./DefinitionAssets"
import { ThoughtBubble } from "./DefinitionType"


export class DefinitionState extends Phaser.State {
    static readonly FONT_TYPE = FontType.ANTON

    background: Phaser.Sprite
    kidHeads: Phaser.Sprite
    defaultPromptText: Phaser.Text
    promptText: Phaser.Text
    redWordsGroup: Phaser.Group
    headsGroup: Phaser.Group
    bubbleDetailGroup: Phaser.Group
    yesButton: Phaser.Text
    yesActive = false
    noButton: Phaser.Text
    noActive = false
    nextButton: ButtonContainer

    canInteractWithBubbles = false
    canClickNext = false
    nextGoesBackToKids = false

    redWords: Array<string> = []

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorBlack(this.game)

        this.game.input.enabled = false
        this.background = Sprites.add(this.game, DefinitionImages.BACKGROUND)
        Scale.fillScreen(this.background)

        this.headsGroup = this.game.add.group()
        this.redWordsGroup = this.game.add.group()
        this.bubbleDetailGroup = this.game.add.group()

        // Prompt text
        log.debug("Add default text")
        this.defaultPromptText = PhaserText.create(this.game, "Some kids feel that people who smoke are...",
            {y: Scale.availableHeight() * 0.08, fontPx: 32, type: DefinitionState.FONT_TYPE, group: this.headsGroup, centerInWorldXOnly: true})

        // Kid heads
        this.kidHeads = Sprites.add(this.game, DefinitionImages.KIDS_SMILE,
            {scale: .17, x: Scale.availableWidth() * 0.17, y: Scale.availableHeight() * .51, group: this.headsGroup})

        const bubbles = {
            "Smart": {
                image: DefinitionImages.BUBBLE_SMART, text: "Smart", antiText: "Dumb", audio: DefinitionAudio.WORDS_SMART,
                detailImage: DefinitionImages.DETAIL_SMART, detailAntiImage: DefinitionImages.DETAIL_SMART_NOT,
                bubbleX: Scale.availableWidth() * -.01, bubbleY: Scale.availableHeight() * .80,
                textX: Scale.availableWidth() * .08, textY: Scale.availableHeight() * .86,
                antiTextX: Scale.availableWidth() * .08, antiTextY: Scale.availableHeight() * .86,
                lookAudio: DefinitionAudio.LOOK_SMART, feelAudio: DefinitionAudio.FEEL_DUMB
            },
            "Popular": {
                image: DefinitionImages.BUBBLE_POPULAR, text: "Popular", antiText: "Unpopular", audio: DefinitionAudio.WORDS_POPULAR,
                detailImage: DefinitionImages.DETAIL_POPULAR, detailAntiImage: DefinitionImages.DETAIL_POPULAR_NOT,
                bubbleX: Scale.availableWidth() * -.02, bubbleY: Scale.availableHeight() * .39,
                textX: Scale.availableWidth() * .06, textY: Scale.availableHeight() * .46,
                antiTextX: Scale.availableWidth() * .04, antiTextY: Scale.availableHeight() * .46,
                lookAudio: DefinitionAudio.LOOK_POPULAR, feelAudio: DefinitionAudio.FEEL_UNPOPULAR
            },
            "Good-looking": {
                image: DefinitionImages.BUBBLE_GOODLOOKING, text: "Good-looking", antiText: "Ugly", audio: DefinitionAudio.WORDS_GOODLOOKING,
                detailImage: DefinitionImages.DETAIL_GOODLOOKING, detailAntiImage: DefinitionImages.DETAIL_GOODLOOKING_NOT,
                bubbleX: Scale.availableWidth() * .21, bubbleY: Scale.availableHeight() * .20,
                textX: Scale.availableWidth() * .255, textY: Scale.availableHeight() * .29,
                antiTextX: Scale.availableWidth() * .32, antiTextY: Scale.availableHeight() * .29,
                lookAudio: DefinitionAudio.LOOK_GOODLOOKING, feelAudio: DefinitionAudio.FEEL_UGLY
            },
            "Grown-up": {
                image: DefinitionImages.BUBBLE_GROWNUP, text: "Grown-up", antiText: "Childish", audio: DefinitionAudio.WORDS_GROWNUP,
                detailImage: DefinitionImages.DETAIL_GROWNUP, detailAntiImage: DefinitionImages.DETAIL_GROWNUP_NOT,
                bubbleX: Scale.availableWidth() * .51, bubbleY: Scale.availableHeight() * .27,
                textX: Scale.availableWidth() * .56, textY: Scale.availableHeight() * .33,
                antiTextX: Scale.availableWidth() * .57, antiTextY: Scale.availableHeight() * .33,
                lookAudio: DefinitionAudio.LOOK_GROWNUP, feelAudio: DefinitionAudio.FEEL_CHILDISH
            },
            "Exciting": {
                image: DefinitionImages.BUBBLE_EXCITING, text: "Exciting", antiText: "Boring", audio: DefinitionAudio.WORDS_EXCITING,
                detailImage: DefinitionImages.DETAIL_EXCITING, detailAntiImage: DefinitionImages.DETAIL_EXCITING_NOT,
                bubbleX: Scale.availableWidth() * .77, bubbleY: Scale.availableHeight() * .40,
                textX: Scale.availableWidth() * .83, textY: Scale.availableHeight() * .48,
                antiTextX: Scale.availableWidth() * .84, antiTextY: Scale.availableHeight() * .48,
                lookAudio: DefinitionAudio.LOOK_EXCITING, feelAudio: DefinitionAudio.FEEL_BORING
            },
            "Cool": {
                image: DefinitionImages.BUBBLE_COOL, text: "Cool", antiText: "Not Cool", audio: DefinitionAudio.WORDS_COOL,
                detailImage: DefinitionImages.DETAIL_COOL, detailAntiImage: DefinitionImages.DETAIL_COOL_NOT,
                bubbleX: Scale.availableWidth() * .75, bubbleY: Scale.availableHeight() * .73,
                textX: Scale.availableWidth() * .82, textY: Scale.availableHeight() * .83,
                antiTextX: Scale.availableWidth() * .795, antiTextY: Scale.availableHeight() * .83,
                lookAudio: DefinitionAudio.LOOK_COOL, feelAudio: DefinitionAudio.FEEL_NOTCOOL
            }
        }
        Object.entries(bubbles).forEach(([, value]) => this.addBubble(this.game, this.headsGroup, this.redWordsGroup, value))

        Audio.add(this.game, DefinitionAudio.NARRATION_INTRO, {
            onStop: () => {
                this.game.input.enabled = true
                this.canInteractWithBubbles = true
            }
        })
    }

    private addBubble(game: Phaser.Game, headsGroup: Phaser.Group, redWordsGroup: Phaser.Group, bubble: ThoughtBubble) {
        const thoughtBubble = new ExpandoSprite(game, bubble.bubbleX, bubble.bubbleY, bubble.image,
            {
                scale: 1.3,
                group: headsGroup,
                onExpand: () => {
                    if (this.canInteractWithBubbles) {
                        Audio.add(game, bubble.audio)
                    }
                },
            }
        )
        const text = PhaserText.create(this.game, bubble.text,
            {
                x: bubble.textX,
                y: bubble.textY,
                fontPx: 30,
                type: DefinitionState.FONT_TYPE,
                color: COLOR.blue,
                group: this.headsGroup
            }
        )
        const antiText = PhaserText.create(this.game, bubble.antiText,
            {
                x: bubble.antiTextX,
                y: bubble.antiTextY,
                fontPx: 30,
                type: DefinitionState.FONT_TYPE,
                color: COLOR.red,
                group: this.redWordsGroup
            }
        )
        antiText.alpha = 0

        // Fade the bubbles in
        thoughtBubble.alpha = 0
        text.alpha = 0
        setTimeout(() => {
            Sprites.fade(thoughtBubble, {fadeDuration: 1000})
            Sprites.fade(text, {fadeDuration: 2000})
        }, 500)

        // When you click a bubble, hide the kids and show the bubble detail with question
        thoughtBubble.addOnClick((clickedBubble) => {
            this.noActive = false
            this.yesActive = false
            if (this.canInteractWithBubbles) {
                clickedBubble.update()

                this.canInteractWithBubbles = false
                headsGroup.forEach(element => Sprites.fadeOut(element))
                redWordsGroup.forEach(element => Sprites.fadeOut(element))

                // Prompt text
                this.promptText = PhaserText.create(this.game, `Does this look ${bubble.text.toLowerCase()} to you?`,
                    {y: Scale.availableHeight() * 0.04, fontPx: 40, type: DefinitionState.FONT_TYPE, group: this.bubbleDetailGroup, centerInWorldXOnly: true})
                const descriptionLen = bubble.text.length
                this.promptText.addColor(COLOR.blue, 15)
                this.promptText.addColor(COLOR.black, 15 + descriptionLen)

                // Yes/No Buttons
                this.yesButton = PhaserText.create(this.game, "YES",
                    {
                        x: Scale.x(0.34), y: Scale.y(0.16), fontPx: 40,
                        type: DefinitionState.FONT_TYPE, group: this.bubbleDetailGroup
                    })

                this.noButton = PhaserText.create(this.game, "NO",
                    {
                        x: Scale.x(0.59), y: Scale.y(0.16), fontPx: 40,
                        type: DefinitionState.FONT_TYPE, group: this.bubbleDetailGroup
                    })

                // Bubble Detail Image
                const bubbleDetail = Sprites.add(this.game, bubble.detailImage, {y: Scale.availableHeight() * 0.3, group: this.bubbleDetailGroup})
                Scale.fillPortionOfScreenWidth(bubbleDetail, .85)
                Sprites.centerInWorldXOnly(bubbleDetail)

                // Next Button
                this.nextButton = Buttons.create(
                    this.game, "Next",
                    () => this.nextClick(this.bubbleDetailGroup, this.headsGroup, this.redWordsGroup, bubbleDetail, bubble.feelAudio,
                        bubble.text, bubble.antiText, bubble.detailAntiImage),
                    {x: Scale.availableWidth() * 0.85, y: Scale.availableHeight() * .9, group: this.bubbleDetailGroup}
                )
                this.nextButton.disable()

                Audio.add(this.game, bubble.lookAudio, {
                    onStop: () => {
                        Audio.add(this.game, DefinitionAudio.NARRATION_CHOOSE_ANSWER, {
                            stopAllAudio: false,
                            onStop: () => {
                                Buttons.makeSpriteButton(this.yesButton, () => this.yesClick(this.nextButton))
                                this.yesButton.events.onInputOver.add(() => {
                                    this.yesButton.addColor(COLOR.redOrange, 0)
                                })
                                this.yesButton.events.onInputOut.add(() => {
                                    this.yesButton.addColor(this.yesActive ? COLOR.blue : COLOR.black, 0)
                                })
                                Buttons.makeSpriteButton(this.noButton, () => this.noClick(this.nextButton))
                                this.noButton.events.onInputOver.add(() => {
                                    this.noButton.addColor(COLOR.redOrange, 0)
                                })
                                this.noButton.events.onInputOut.add(() => {
                                    this.noButton.addColor(this.noActive ? COLOR.blue : COLOR.black, 0)
                                })
                            }
                        })
                    }
                })
            }
        })

    }

    private yesClick = (nextButton: ButtonContainer) => {
        nextButton.enable()
        this.yesButton.addColor(COLOR.blue, 0)
        this.noButton.addColor(COLOR.black, 0)
        this.yesActive = true
        this.noActive = false
        this.canClickNext = true
    }

    private noClick = (nextButton: ButtonContainer) => {
        nextButton.enable()
        this.yesButton.addColor(COLOR.black, 0)
        this.noButton.addColor(COLOR.blue, 0)
        this.yesActive = false
        this.noActive = true
        this.canClickNext = true
    }

    private nextClick(bubbleDetailGroup: Phaser.Group, headsGroup: Phaser.Group, redWordsGroup: Phaser.Group,
                      bubbleDetail: Phaser.Sprite, feelAudio: string, text: string, antiText: string, detailAntiImage: string) {
        if (this.canClickNext) {
            if (this.nextGoesBackToKids) {
                this.canClickNext = false
                // Destroy positive attribute word, disable corresponding bubble, and show negative attribute words
                headsGroup.children.forEach((element: Phaser.Text) => {
                    if (element.text === text) {
                        element.destroy()
                    }
                })
                headsGroup.children.forEach((element: ExpandoSprite) => {
                    if (element.key === `bubble-${text.toLowerCase()}.png`) {
                        element.on = false
                    }
                })
                this.redWords.push(antiText)
                redWordsGroup.children.forEach((element: Phaser.Text) => {
                    if (this.redWords.includes(element.text)) {
                        Sprites.fade(element, {fadeDuration: 0})
                    }
                })

                // When all bubbles checked, kids frown and exit process begins
                let nextAudio = DefinitionAudio.NARRATION_CLICK_ANOTHER_WORD
                let showFinishButton = false
                if (this.redWords.length >= 6) {
                    log.debug("Add ending text")
                    this.defaultPromptText.destroy()
                    PhaserText.create(this.game, "Now you know that most kids feel smokers are...",
                        {y: Scale.availableHeight() * 0.08, fontPx: 32, type: DefinitionState.FONT_TYPE, group: this.headsGroup, centerInWorldXOnly: true})
                    this.kidHeads.destroy()
                    this.kidHeads = Sprites.add(this.game, DefinitionImages.KIDS_FROWN,
                        {scale: .17, x: Scale.availableWidth() * 0.17, y: Scale.availableHeight() * .51, group: this.headsGroup})
                    nextAudio = DefinitionAudio.NARRATION_OUTRO
                    showFinishButton = true
                }

                // Hide bubble detail group and show kid faces and bubbles
                bubbleDetailGroup.forEach(element => Sprites.fadeOut(element))
                headsGroup.forEach(element => Sprites.fade(element))
                Audio.add(this.game, nextAudio, {
                    onPlay: () => this.game.input.enabled = false,
                    onStop: () => {
                        this.game.input.enabled = true
                        this.canInteractWithBubbles = true
                        if (showFinishButton) {
                            Buttons.create(
                                this.game, "Next",
                                () => Transitions.startOutro(this.game, ActivityKey.DEFINITION),
                                {x: Scale.availableWidth() * .45, y: Scale.availableHeight() * .9, group: this.bubbleDetailGroup}
                            )
                        }
                    }
                })
            } else {
                this.promptText.destroy()
                this.yesButton.destroy()
                this.noButton.destroy()
                this.nextButton.disable()

                this.promptText = PhaserText.create(this.game, `Most kids feel that people who smoke are ${antiText.toLowerCase()}`,
                    {y: Scale.availableHeight() * 0.06, fontPx: 30, type: DefinitionState.FONT_TYPE, group: this.bubbleDetailGroup, centerInWorldXOnly: true})
                this.promptText.addColor(COLOR.blue, 0)
                this.promptText.addColor(COLOR.black, 4)

                Audio.add(this.game, feelAudio, {
                    onStop: () => {
                        Audio.add(this.game, DefinitionAudio.NARRATION_CLICK_NEXT, {
                            stopAllAudio: false,
                            onStop: () => {
                                this.nextButton.enable()
                            }
                        })
                    }
                })

                // Replace bubble detail with counterclaim
                setTimeout(() => {
                    bubbleDetail.destroy()
                    const bubbleDetailNot = Sprites.add(this.game, detailAntiImage, {y: Scale.availableHeight() * 0.3, group: this.bubbleDetailGroup})
                    Scale.fillPortionOfScreenWidth(bubbleDetailNot, .85)
                    Sprites.centerInWorldXOnly(bubbleDetailNot)
                }, 1900)
            }
            this.nextGoesBackToKids = !this.nextGoesBackToKids
        }
    }
}
